import { useMemo } from 'react';
import { BarChart, DonutChart, LineChart } from '../../../../lib/charts/index.js';
import { faChartValue } from '../format.js';
import type { ChartSpec, DrillAction } from '../types.js';

export interface ChartBlockProps {
  readonly spec: ChartSpec;
  readonly onDrill?: ((action: DrillAction) => void) | undefined;
  readonly disabled?: boolean | undefined;
}

/** The design caps a donut at five slices — anything beyond is merged into «سایر». */
const MAX_DONUT_SLICES = 5;

function limitSlices(data: readonly { label: string; value: number }[]) {
  if (data.length <= MAX_DONUT_SLICES) return data;
  const head = data.slice(0, MAX_DONUT_SLICES - 1);
  const rest = data.slice(MAX_DONUT_SLICES - 1);
  return [...head, { label: 'سایر', value: rest.reduce((sum, slice) => sum + slice.value, 0) }];
}

/**
 * Component 3 — line (trend) / bar (comparison) / donut (share, ≤ 5 slices),
 * rendered with the shared ChartKit (ECharts, theme-aware). Tooltips show the
 * exact figure of the datum. The visible caption carries the unit.
 *
 * Provides accessible drill-through targets into the authorized documents of that scope.
 */
export function ChartBlock({ spec, onDrill, disabled = false }: ChartBlockProps) {
  const suffix = spec.valueSuffix ?? '';
  const valueFormatter = useMemo(
    () => (value: number) => `${faChartValue(value)}${suffix}`,
    [suffix],
  );
  const donutData = useMemo(() => (spec.kind === 'donut' ? limitSlices(spec.data) : []), [spec]);

  const drillItems = useMemo(() => {
    if (!onDrill) return [];
    if (spec.kind === 'line') {
      return spec.categories.map((cat) => ({ id: cat, label: cat }));
    }
    if (spec.kind === 'donut') {
      return donutData.filter((d) => d.label !== 'سایر').map((d) => ({ id: d.label, label: d.label }));
    }
    return spec.data.map((d) => ({ id: d.label, label: d.label }));
  }, [onDrill, spec, donutData]);

  return (
    <figure className={`ai-block ai-chart ai-chart--${spec.kind}`}>
      <figcaption className="ai-chart__caption">{spec.caption}</figcaption>
      {spec.kind === 'line' ? (
        <LineChart
          categories={spec.categories}
          series={spec.series}
          ariaLabel={spec.ariaLabel}
          height={spec.height ?? 200}
          valueFormatter={valueFormatter}
        />
      ) : spec.kind === 'bar' ? (
        <BarChart
          data={spec.data}
          ariaLabel={spec.ariaLabel}
          height={spec.height ?? 200}
          valueFormatter={valueFormatter}
        />
      ) : (
        <DonutChart
          data={donutData}
          ariaLabel={spec.ariaLabel}
          height={spec.height ?? 230}
          centerLabel={spec.centerLabel ?? 'مجموع'}
          valueFormatter={valueFormatter}
        />
      )}
      {onDrill && drillItems.length > 0 ? (
        <div className="ai-chart__drills" role="group" aria-label="ریزشدن در اسناد">
          <span className="ai-chart__drills-label">ریز اسناد:</span>
          <div className="ai-chart__drills-chips">
            {drillItems.slice(0, 5).map((item) => (
              <button
                key={item.id}
                type="button"
                className="sk-btn sk-btn--secondary sk-btn--sm ai-pill"
                disabled={disabled}
                onClick={() =>
                  onDrill({
                    kind: 'entity',
                    entityId: item.label,
                    label: `ریز اسناد ${item.label}`,
                  })
                }
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </figure>
  );
}
